/**
 * Auto-fit arbitration for the Smart Calendar's zoom level (VE-2110).
 *
 * Bryntum already persists zoom on its own: `TimelineState.getState` writes `zoomLevel` (plus the
 * tick size and date range under `zoomLevelOptions`) into the widget state, `applyState` restores
 * it through `zoomToLevel`, and `SchedulerState` lists `dateRangeChange` in `statefulEvents` so a
 * zoom the user performs is saved without anything here asking for it.
 *
 * The bug was never missing persistence — it was that the calendar re-ran `zoomToFit` on EVERY
 * project `refresh`, which lands after the restore and throws the restored level away. Because
 * `zoomToFit` derives the level from the container width and the data span, the level a user
 * ended up at was a function of their window size rather than of anything they chose.
 *
 * So the fix is subtractive: keep the fit for the one case it exists to serve — a calendar that
 * has never been zoomed, which would otherwise open on the hardcoded `weekAndDayLetter` preset —
 * and stay out of the way once a level has been stored. `shouldAutoFitZoom` is the whole of that
 * decision, kept pure so the rules are testable without mounting Bryntum.
 */

/** The prefix Bryntum's local `StateProvider` puts in front of every `stateId`. */
export const BRYNTUM_STATE_PREFIX = 'bryntum-state:';

/**
 * Builds the localStorage key holding one Gantt's persisted Bryntum widget state.
 * @param {string} stateId - The `stateId` handed to the `BryntumGantt`.
 * @returns {string} The full storage key, prefix included.
 */
export const bryntumStateKey = (stateId: string): string => `${BRYNTUM_STATE_PREFIX}${stateId}`;

/**
 * Reports whether Bryntum has a usable zoom level saved for this calendar.
 *
 * The stored blob is untrusted: it can be absent on a first visit, corrupt from an interrupted
 * write or a hand edit, and `getItem` itself throws in some privacy modes. Any of those means
 * "no stored zoom", which reopens the auto-fit rather than breaking the calendar. A non-finite
 * number is rejected too — `zoomToLevel(NaN)` would leave the timeline blank.
 * @param {string} stateId - The `stateId` handed to the `BryntumGantt`.
 * @returns {boolean} Whether a zoom level worth restoring is on disk.
 */
export const hasStoredZoomLevel = (stateId: string): boolean => {
  try {
    const raw = localStorage.getItem(bryntumStateKey(stateId));
    if (raw == null || raw === '') return false;

    const parsed = JSON.parse(raw);
    if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) return false;

    const { zoomLevel } = parsed as { zoomLevel?: unknown };
    return typeof zoomLevel === 'number' && Number.isFinite(zoomLevel);
  } catch (_e) {
    return false;
  }
};

/** The facts `shouldAutoFitZoom` arbitrates between, gathered at the moment a refresh lands. */
export interface AutoFitZoomContext {
  /** Whether {@link hasStoredZoomLevel} found a level Bryntum will restore. */
  hasStoredZoom: boolean;
  /** Whether the task store actually holds rows yet. */
  hasTasks: boolean;
  /** Whether this mount has already spent its one auto-fit. */
  alreadyFitted: boolean;
}

/**
 * Decides whether a project refresh should trigger `zoomToFit`.
 *
 * Three conditions, each earning its place:
 *
 * - `hasStoredZoom` — a stored level is the user's level (Bryntum saves the one they zoomed to,
 *   and equally the one the first-ever auto-fit produced). Fitting over it is the defect.
 * - `hasTasks` — the first refresh of a mount fires before the renewals request resolves. Fitting
 *   an empty project produces a meaningless range and, worse, spends the latch below on it, so
 *   the real data would never get its fit.
 * - `alreadyFitted` — one fit per mount. Without it, a filter change re-fits and wipes out the
 *   zoom the user set moments earlier, which is the same defect by another route.
 * @param {AutoFitZoomContext} context - The facts observed at this refresh.
 * @returns {boolean} Whether to call `zoomToFit` for this refresh.
 */
export const shouldAutoFitZoom = ({ hasStoredZoom, hasTasks, alreadyFitted }: AutoFitZoomContext): boolean =>
  !hasStoredZoom && hasTasks && !alreadyFitted;
